import React, { useState } from 'react';

const VonScalperPricing = () => {
  const [billingCycle, setBillingCycle] = useState('lifetime');

  const plans = [
    {
      name: "Starter",
      description: "Perfect for individual traders getting started",
      price: billingCycle === 'monthly' ? 197 : 497,
      originalPrice: billingCycle === 'monthly' ? null : 997,
      period: billingCycle === 'monthly' ? '/month' : 'one-time',
      popular: false,
      features: [
        "Single MT5 Account License",
        "Multi-Timeframe Analysis (15M/5M/1M)",
        "10+ ICT Pattern Recognition",
        "Basic Risk Management Suite",
        "Telegram Notifications",
        "Multi-Market Support (Forex/Crypto/V75)",
        "Email Support",
        "30-Day Money Back Guarantee"
      ],
      color: "from-slate-600 to-slate-700",
      textColor: "text-slate-800",
      bgColor: "bg-white"
    },
    {
      name: "Professional",
      description: "For serious traders and multi-account management",
      price: billingCycle === 'monthly' ? 397 : 997,
      originalPrice: billingCycle === 'monthly' ? null : 1997,
      period: billingCycle === 'monthly' ? '/month' : 'one-time',
      popular: true,
      features: [
        "3 MT5 Account Licenses",
        "All Starter Features",
        "Adaptive ML Framework",
        "Trend Exhaustion Detection",
        "Advanced Risk Management",
        "Copy Trading Infrastructure",
        "Bloomberg-Style Dashboard",
        "Performance Analytics",
        "Priority Support",
        "Lifetime Updates"
      ],
      color: "from-cyan-600 to-purple-600",
      textColor: "text-white",
      bgColor: "bg-gradient-to-br from-cyan-600 to-purple-600"
    },
    {
      name: "Enterprise",
      description: "For prop firms and institutional traders",
      price: "Custom",
      period: "contact sales",
      popular: false,
      features: [
        "Unlimited MT5 Licenses",
        "All Professional Features",
        "White Label Options",
        "Custom Pattern Development",
        "API Integration",
        "Dedicated Account Manager",
        "Custom Risk Parameters",
        "Priority Development Queue",
        "24/7 Phone Support",
        "On-site Training Available"
      ],
      color: "from-purple-600 to-pink-600",
      textColor: "text-purple-800",
      bgColor: "bg-white"
    }
  ];

  const addons = [
    {
      name: "VPS Hosting",
      description: "Dedicated trading VPS with 99.9% uptime",
      price: 29,
      period: "/month"
    },
    {
      name: "Advanced Analytics",
      description: "Deep performance insights and backtesting",
      price: 97,
      period: "one-time"
    },
    {
      name: "Personal Training",
      description: "1-on-1 setup and optimization session",
      price: 297,
      period: "per session"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-purple-50" id="pricing">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="h-0.5 w-12 bg-gradient-to-r from-cyan-500 to-purple-500 mr-4"></div>
            <span className="py-2 px-4 rounded-full text-purple-700 bg-purple-100 font-semibold text-sm border border-purple-200">PRICING</span>
            <div className="h-0.5 w-12 bg-gradient-to-r from-purple-500 to-cyan-500 ml-4"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-purple-800 bg-clip-text text-transparent">
            Invest in Your Trading Success
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Choose the plan that empowers your trading style and goals. All plans include 
            our 30-day money-back guarantee.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-xl p-2 shadow-lg border border-slate-200">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white shadow-md' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('lifetime')}
              className={`px-6 py-2 rounded-lg font-semibold transition-all relative ${
                billingCycle === 'lifetime' 
                  ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white shadow-md' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Lifetime
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                50% OFF
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Plans */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-3xl p-8 transition-transform hover:scale-105 ${
                plan.popular 
                  ? 'ring-4 ring-purple-500 ring-opacity-50 shadow-2xl transform scale-105' 
                  : 'shadow-xl'
              } ${plan.bgColor}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-cyan-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="space-y-6">
                {/* Plan Header */}
                <div className="text-center">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.textColor}`}>{plan.name}</h3>
                  <p className={`${plan.popular ? 'text-white/80' : 'text-slate-600'} mb-6`}>
                    {plan.description}
                  </p>
                  
                  <div className="mb-6">
                    {plan.price === "Custom" ? (
                      <div className={`text-4xl font-bold ${plan.textColor}`}>Custom</div>
                    ) : (
                      <div className="flex items-baseline justify-center">
                        {plan.originalPrice && (
                          <span className={`text-xl line-through mr-2 ${plan.popular ? 'text-white/60' : 'text-slate-400'}`}>
                            ${plan.originalPrice}
                          </span>
                        )}
                        <span className={`text-5xl font-bold ${plan.textColor}`}>${plan.price}</span>
                        <span className={`text-lg ml-1 ${plan.popular ? 'text-white/80' : 'text-slate-600'}`}>
                          {plan.period}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${plan.popular ? 'text-white' : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className={`${plan.popular ? 'text-white/90' : 'text-slate-700'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <div className="pt-6">
                  {plan.price === "Custom" ? (
                    <button className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg">
                      Contact Sales
                    </button>
                  ) : (
                    <a href='/investregistration' className={`w-full py-4 px-6 font-bold rounded-xl transition-all shadow-lg ${
                      plan.popular 
                        ? 'bg-white text-purple-600 hover:bg-gray-50' 
                        : 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white hover:from-cyan-700 hover:to-purple-700'
                    }`}>
                      Get Started Now
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800">Optional Add-ons</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {addons.map((addon, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
                <h4 className="text-xl font-bold text-slate-800 mb-2">{addon.name}</h4>
                <p className="text-slate-600 mb-4">{addon.description}</p>
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold text-purple-600">${addon.price}</span>
                  <span className="text-slate-500 ml-1">{addon.period}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Guarantee */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center bg-green-50 rounded-2xl p-6 border border-green-200">
            <svg className="w-12 h-12 text-green-500 mr-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="text-left">
              <h4 className="text-xl font-bold text-slate-800">30-Day Money-Back Guarantee</h4>
              <p className="text-slate-600">If VON SCALPER doesn't meet your expectations, we'll refund your investment, no questions asked.</p>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 flex justify-center items-center space-x-8 text-slate-400">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span className="text-sm">Secure Payment</span>
          </div>
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            <span className="text-sm">Instant Delivery</span>
          </div>
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-sm">24/7 Support</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VonScalperPricing;